import { useEffect, useState } from "react";
import { Bell, BellOff, X, Check, TestTube2 } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  isIos,
  isStandalone,
  getCurrentSubscription,
  subscribePush,
  unsubscribePush,
  testPush,
  serverSubscriptionStatus,
  pushDiagnostics,
} from "@/lib/push";

/**
 * Ikonka zvonečku v hlavičce.
 * - Klik → panel se stavem a přepínačem povolení.
 * - Uloží subscription na server (per user).
 * - Automaticky detekuje, když prohlížeč má subscription ale server ne (out-of-sync).
 */
export default function BellButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [serverSubscribed, setServerSubscribed] = useState(false);
  const [permission, setPermission] = useState("default");
  const [supported, setSupported] = useState(true);
  const [needsHomeScreen, setNeedsHomeScreen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [showDiag, setShowDiag] = useState(false);

  const refresh = async () => {
    if (!isPushSupported()) {
      setSupported(false);
      // Na iOS možná jen chybí instalace na plochu
      if (isIos() && !isStandalone()) setNeedsHomeScreen(true);
      return;
    }
    if (isIos() && !isStandalone()) {
      setNeedsHomeScreen(true);
    }
    try {
      setPermission(Notification.permission);
      const sub = await getCurrentSubscription();
      setSubscribed(!!sub);
      if (sub) {
        try {
          const st = await serverSubscriptionStatus();
          setServerSubscribed(!!st?.subscribed);
        } catch {
          setServerSubscribed(false);
        }
      } else {
        setServerSubscribed(false);
      }
    } catch {
      setSubscribed(false);
      setServerSubscribed(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifikace povoleny na tomto zařízení");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Nepodařilo se povolit notifikace");
    } finally {
      setBusy(false);
    }
  };

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Notifikace zrušeny");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      // Před testem — pokud je out-of-sync (browser má, server ne), zkus resync
      if (subscribed && !serverSubscribed) {
        try { await subscribePush(); await refresh(); } catch { /* pokračuj */ }
      }
      const r = await testPush();
      const sent = r.data?.sent || 0;
      if (sent > 0) toast.success(`Test push odeslán (${sent} zařízení)`);
      else toast.error("Server nemá tvé zařízení. Zkus vypnout a znovu zapnout.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  const doResync = async () => {
    setBusy(true);
    try {
      // Nejdřív odhlas úplně (browser + server), pak přihlas znovu
      await unsubscribePush();
      await subscribePush();
      toast.success("Notifikace zresetovány");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  const loadDiag = async () => {
    try {
      const d = await pushDiagnostics();
      setDiag(d);
      setShowDiag(true);
    } catch (e) {
      toast.error(e?.message || "Chyba");
    }
  };

  const Icon = subscribed && serverSubscribed ? Bell : BellOff;
  const color = subscribed && serverSubscribed ? "#376A4D" : subscribed ? "#C29329" : "#57534E";
  const outOfSync = subscribed && !serverSubscribed;

  return (
    <div className="relative">
      <button
        data-testid="bell-button"
        onClick={() => setOpen((o) => !o)}
        className="p-2 rounded-md hover:bg-[#F9F6F0] relative"
        title={
          outOfSync ? "Notifikace nesynchronizované — otevři pro reset"
          : subscribed ? "Notifikace zapnuty"
          : "Notifikace vypnuty"
        }
        aria-label="Notifikace"
      >
        <Icon className="w-5 h-5" style={{ color }} />
        {(subscribed || outOfSync) && (
          <span
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
            style={{ background: outOfSync ? "#C29329" : "#376A4D" }}
          />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="bell-panel"
            className="absolute right-0 top-full mt-2 w-80 max-w-[92vw] bg-white border border-[#E5E1D8] rounded-lg shadow-xl z-50 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow mb-0.5">Push notifikace</div>
                <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
                  Na tomto zařízení
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[#F9F6F0]" aria-label="Zavřít">
                <X className="w-4 h-4" />
              </button>
            </div>

            {needsHomeScreen && (
              <div className="text-sm text-[#9E4747] mb-3 leading-relaxed">
                <strong>iPhone / iPad:</strong> Nejprve appku přidej na plochu:
                <ol className="text-xs mt-1 space-y-0.5 list-decimal ml-4">
                  <li>Klepni na <strong>Sdílet</strong> (čtverec se šipkou)</li>
                  <li>Vyber <strong>Přidat na plochu</strong></li>
                  <li>Otevři appku z ikony na ploše</li>
                  <li>Pak zapni notifikace</li>
                </ol>
              </div>
            )}

            {!supported && !needsHomeScreen && (
              <div className="text-sm text-[#9E4747] mb-2">
                Tento prohlížeč nepodporuje push notifikace.
              </div>
            )}

            {supported && !needsHomeScreen && (
              <>
                <div className="text-xs text-[#57534E] mb-3">
                  Stav:{" "}
                  {outOfSync ? (
                    <span className="text-[#C29329] font-medium">
                      Neshoda s serverem — proveď reset
                    </span>
                  ) : subscribed && serverSubscribed ? (
                    <span className="text-[#376A4D] font-medium inline-flex items-center gap-1">
                      <Check className="w-3 h-3" /> Zapnuto
                    </span>
                  ) : permission === "denied" ? (
                    <span className="text-[#9E4747] font-medium">Zablokováno</span>
                  ) : (
                    <span className="text-[#57534E] font-medium">Vypnuto</span>
                  )}
                </div>

                {permission === "denied" ? (
                  <div className="text-xs text-[#57534E] leading-relaxed">
                    Notifikace jsou zablokované. Otevři nastavení stránky a povol je manuálně.
                  </div>
                ) : outOfSync ? (
                  <div className="space-y-2">
                    <div className="text-xs text-[#57534E] leading-relaxed">
                      Prohlížeč má aktivní odběr, ale server tvé zařízení nezná (pravděpodobně bylo
                      smazáno nebo se subscription neuložila). Reset to spraví.
                    </div>
                    <button
                      data-testid="bell-resync-btn"
                      disabled={busy}
                      onClick={doResync}
                      className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
                    >
                      Reset notifikací (znovu přihlásit)
                    </button>
                  </div>
                ) : subscribed ? (
                  <div className="space-y-2">
                    <button
                      data-testid="bell-test-btn"
                      disabled={busy}
                      onClick={doTest}
                      className="w-full py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] text-sm inline-flex items-center justify-center gap-1.5"
                    >
                      <TestTube2 className="w-4 h-4" /> Poslat testovací push
                    </button>
                    <button
                      data-testid="bell-unsubscribe-btn"
                      disabled={busy}
                      onClick={doUnsubscribe}
                      className="w-full py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] text-sm text-[#9E4747]"
                    >
                      Zrušit notifikace na tomto zařízení
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-[#57534E] leading-relaxed mb-3">
                      Zapneš připomenutí odchodu ze směny a další notifikace, které nastavil admin.
                    </div>
                    <button
                      data-testid="bell-subscribe-btn"
                      disabled={busy}
                      onClick={doSubscribe}
                      className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
                    >
                      <Bell className="w-4 h-4" /> Zapnout notifikace
                    </button>
                  </>
                )}
              </>
            )}

            {/* Diagnostika — vždy dostupná pro debug */}
            <div className="mt-4 pt-3 border-t border-[#E5E1D8]">
              <button
                data-testid="bell-diag-btn"
                onClick={loadDiag}
                className="text-xs text-[#57534E] hover:text-[#3A2D27] underline"
              >
                {showDiag ? "Skrýt" : "Zobrazit"} diagnostiku
              </button>
              {showDiag && diag && (
                <div className="mt-2 text-[11px] text-[#57534E] space-y-0.5 leading-relaxed">
                  <div>
                    Server VAPID:{" "}
                    <strong className={diag.server_has_vapid_public && diag.server_has_vapid_private ? "text-[#376A4D]" : "text-[#9E4747]"}>
                      {diag.server_has_vapid_public && diag.server_has_vapid_private ? "OK" : "CHYBÍ"}
                    </strong>
                    {!diag.server_has_vapid_public && " (chybí VAPID_PUBLIC_KEY)"}
                    {!diag.server_has_vapid_private && " (chybí VAPID_PRIVATE_KEY_PEM)"}
                    {!diag.server_has_vapid_subject && " (chybí VAPID_SUBJECT)"}
                  </div>
                  <div>
                    Uložených zařízení pro tento účet:{" "}
                    <strong className={diag.total > 0 ? "text-[#376A4D]" : "text-[#9E4747]"}>{diag.total}</strong>
                  </div>
                  {diag.subscriptions_in_db?.length > 0 && (
                    <ul className="mt-1 space-y-1">
                      {diag.subscriptions_in_db.map((s, i) => (
                        <li key={i} className="bg-[#F9F6F0] p-1.5 rounded border border-[#E5E1D8]">
                          <div className="mono truncate">{s.endpoint_prefix}</div>
                          <div className="opacity-70 truncate">{s.user_agent}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
